import React, { useEffect, useState } from 'react'

import classnames from 'classnames'

import { PictureCaptioned } from '../../../../Picture'
import type { Images } from '../../imageType'
import { ZoomImageHint } from '.'
import styles from './Carousel.module.scss'

export const SlideImage = ({
  alt,
  carouselBgColor,
  credit,
  src,
  sources,
  zoomSources,
  zoomSrc,
  setPortalConfig,
}: SlideImageArgs) => {
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  if (!hasMounted) {
    return null
  }

  const isTouchDevice = () => {
    if (typeof window !== 'undefined') {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0
    }
    return false
  }
  const touchDevice = isTouchDevice()
  const bgIsWhite = carouselBgColor === 'white'

  const zoom = (e: React.MouseEvent<HTMLDivElement>) => {
    const zoomer = e.currentTarget || e.target
    if (!zoomer) {
      return
    }
    const offsetX = e.clientX - zoomer.getBoundingClientRect().left
    const offsetY = e.clientY - zoomer.getBoundingClientRect().top

    const x = (offsetX / zoomer.offsetWidth) * 100
    const y = (offsetY / zoomer.offsetHeight) * 100
    zoomer.style.backgroundPosition = `${x}% ${y}%`
  }

  if (zoomSrc && !touchDevice) {
    return (
      <figure
        className={classnames(styles.zoom, bgIsWhite && styles.whiteBackground)}
        onMouseMove={(e: React.MouseEvent<HTMLDivElement>) => zoom(e)}
        style={{
          backgroundImage: `url(${zoomSources?.large ?? zoomSrc})`,
        }}
      >
        <picture>
          <source
            srcSet={zoomSources?.large ?? zoomSrc}
            media="(min-width: 768px)"
          />
          <source
            srcSet={zoomSources?.medium ?? src}
            media="(max-width: 767px)"
          />
          <img src={zoomSources?.medium ?? src} alt={alt} />
        </picture>
        <ZoomImageHint />
      </figure>
    )
  }

  if (zoomSrc && touchDevice) {
    const handleClickOnMobile = () => {
      setPortalConfig({ displayPortal: true, zoomSrc, zoomSources, alt })
    }

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div
        data-testid="with-zoomSrc-mobile-container"
        onClick={handleClickOnMobile}
      >
        <PictureCaptioned
          src={src}
          alt={alt}
          credit={credit}
          sources={sources?.main}
          imageClassName={bgIsWhite ? styles.whiteBackground : undefined}
        />
        <ZoomImageHint />
      </div>
    )
  }

  return (
    <PictureCaptioned
      src={src}
      alt={alt}
      credit={credit}
      sources={sources?.main}
      imageClassName={bgIsWhite ? styles.whiteBackground : undefined}
    />
  )
}

type SlideImageArgs = Images & {
  setPortalConfig: SetPortalConfig
  carouselBgColor: 'grey' | 'white'
}

export type SetPortalConfig = React.Dispatch<
  React.SetStateAction<{
    zoomSrc?: Images['zoomSrc']
    zoomSources?: Images['zoomSources']
    alt?: Images['alt']
    displayPortal: boolean
  }>
>
